import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';

// ── Global tap-feedback ripple ───────────────────────────────────────────────────────────────
// A tiny, always-on, top-most overlay scene launched once at boot. On EVERY pointer-down anywhere
// on the canvas it spawns a small emerald ring that expands + fades out at the touch point, so
// mobile players get instant confirmation that their tap registered.
//
// It is PURELY visual:
//   • The scene listens to its own InputPlugin's POINTER_DOWN, which Phaser dispatches for every
//     pointer regardless of what game object is hit — so it never needs (and never has) interactive
//     objects, and therefore can never win a hit-test or swallow a tap from the scenes below.
//   • The ring Graphics is added with `setScrollFactor(0)` + a very high depth and is explicitly
//     non-interactive, so it sits above all UI but blocks nothing.
//
// Subtle + premium: a thin emerald ring, ~280ms ease-out, capped concurrency so rapid taps stay
// light. Harmless on desktop (a mouse click just produces the same gentle ring).

const EMERALD = 0x34d399;
const DEPTH = 100000; // above every other scene/object
const RING_R = 20; // base ring radius (px); expansion is done via scale so the stroke redraws
const RING_START_SCALE = 0.3; // scale at spawn
const RING_END_SCALE = 1.7; // scale at the end of the expand
const RING_DURATION = 280; // ms
const MAX_CONCURRENT = 6; // cap live rings so a flurry of taps can't pile up

export class TapFeedbackScene extends Phaser.Scene {
  /** @type {Set<Phaser.GameObjects.Arc>} live ring graphics (for concurrency capping) */
  #rings = new Set();

  constructor() {
    super({ key: SCENE_KEYS.TAP_FEEDBACK_SCENE });
  }

  /** @returns {void} */
  create() {
    // Global: fires for every pointer-down on the canvas, across all scenes/overlays.
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.#spawnRing, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off(Phaser.Input.Events.POINTER_DOWN, this.#spawnRing, this);
    });
    // Keep this overlay pinned to the very top of the render order.
    this.scene.bringToTop();
  }

  /**
   * Spawn an expanding, fading emerald ring at the pointer location.
   * @param {Phaser.Input.Pointer} pointer
   * @returns {void}
   */
  #spawnRing(pointer) {
    // Cap concurrency: if we're at the limit, drop the oldest live ring before adding a new one.
    if (this.#rings.size >= MAX_CONCURRENT) {
      const oldest = this.#rings.values().next().value;
      if (oldest) {
        this.#rings.delete(oldest);
        oldest.destroy();
      }
    }

    // Other scenes call bringToTop() on themselves over their lifetime, which can render them above
    // this overlay. Re-assert top-most right before drawing so the ring is always visible. Cheap.
    this.scene.bringToTop();

    const ring = this.add
      .circle(pointer.x, pointer.y, RING_R)
      .setStrokeStyle(2, EMERALD, 0.9)
      .setFillStyle()
      .setScrollFactor(0)
      .setDepth(DEPTH)
      .setScale(RING_START_SCALE);
    this.#rings.add(ring);

    // Expand via SCALE (not radius — an Arc's geometry is baked at creation, so tweening `radius`
    // wouldn't redraw the stroke) and fade alpha to 0. Cubic ease-out for a snappy, premium feel.
    this.tweens.add({
      targets: ring,
      scale: RING_END_SCALE,
      alpha: 0,
      duration: RING_DURATION,
      ease: Phaser.Math.Easing.Cubic.Out,
      onComplete: () => {
        this.#rings.delete(ring);
        ring.destroy();
      },
    });
  }
}
